/**
 * Detect Facebook / Instagram / Messenger in-app browsers (webviews) and break
 * out of them to the device's real browser (Chrome / Safari).
 *
 * Why this matters for the FB funnel: most ad traffic opens inside the
 * Facebook/Instagram in-app browser. Two separate problems:
 *   1. Google OAuth is BLOCKED in those webviews (Google returns
 *      "disallowed_useragent"), so the funnel hides "Continue with Google"
 *      there and lets the parent use email instead (works fine in a webview).
 *   2. The Stripe checkout handoff CRASHES in the FB Android webview
 *      ("Error invoking postMessage: Java exception ...") and the webview has
 *      no Apple Pay / Google Pay, so conversion at checkout is ~9% vs ~29% in a
 *      normal browser. The fix: when the user taps pay, escape to the external
 *      browser and open Stripe there instead of navigating the webview to it.
 *
 * The global InAppBrowserOverlay separately nudges users to open a real
 * browser; these helpers let the funnel adapt inline and break out on demand.
 */

// Facebook (FBAN/FBAV/FB_IAB/FBIOS/FBDV), Instagram, and Messenger (Orca on
// Android, "Messenger" on iOS) all report a distinctive user-agent token.
const IN_APP_UA = /FBAN|FBAV|FB_IAB|FBIOS|FBDV|Instagram|Messenger|Orca/i;

export function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  return IN_APP_UA.test(navigator.userAgent || '');
}

/**
 * Best-effort OS bucket for the current in-app browser: 'ios' | 'android' |
 * 'other'. iPadOS reports as "Macintosh" with touch, so treat that as iOS too.
 */
export function getInAppBrowserPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document);
  if (isIOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * Attempt to open `url` in the device's real external browser, escaping the
 * in-app webview. Returns { platform, method, needsInstructions } describing
 * what was tried so the caller can track it and, on iOS, show a short hint.
 *
 *   - Android: navigate to an intent:// URL with no package set, which hands
 *     off to the user's DEFAULT browser. This is the reliable escape from the
 *     FB Android webview. method: 'intent'.
 *   - iOS: there is no reliable programmatic escape from the FB/IG webview.
 *     We still attempt window.open(url) first (FB sometimes honors it), then
 *     signal the caller to show "open in external browser" instructions.
 *     method: 'window_open', needsInstructions: true.
 *   - other: plain window.open(url) in a new tab. method: 'window_open'.
 */
export function breakOutToExternalBrowser(url) {
  const platform = getInAppBrowserPlatform();

  if (platform === 'android') {
    try {
      const stripped = String(url).replace(/^https?:\/\//, '');
      // scheme=https so the target browser reopens it as a normal https URL;
      // no package= so it lands in the user's default browser, not just Chrome.
      window.location.href = `intent://${stripped}#Intent;scheme=https;end`;
      return { platform, method: 'intent', needsInstructions: false };
    } catch {
      /* fall through to window.open below */
    }
  }

  // iOS and everything else: try a new-tab open. On iOS this often just spawns
  // another in-app tab, so the caller also shows escape instructions.
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    /* non-fatal */
  }
  return {
    platform,
    method: 'window_open',
    needsInstructions: platform === 'ios',
  };
}
